// Activation, sign-up, sign-in, the code, passkeys and the error screens.
import { useEffect, useState } from 'preact/hooks'
import { browserSupportsWebAuthn, startAuthentication, startRegistration, type PublicKeyCredentialCreationOptionsJSON, type PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser'
import type { FlowView, Me } from '../../../packages/contract/index.ts'
import { fill } from './i18n.ts'
import { ApiError, api, fmtTime, mmss, platform } from './lib.ts'
import { Brand, ContactSheet, Icon, Inbox, Otp, SocialIcon, SocialSheet, Spinner, useApp, type OtpState, type Social } from './ui.tsx'

// ───────────── passkeys ─────────────

export const passkeysWork = (): boolean => { try { return browserSupportsWebAuthn() } catch { return false } }

/** The customer closed the passkey prompt or it timed out: a quiet state, never an error. */
const cancelled = (error: unknown): boolean => {
  const e = error as { name?: string; code?: string; cause?: { name?: string } }
  return e?.name === 'NotAllowedError' || e?.name === 'AbortError' || e?.cause?.name === 'NotAllowedError' || e?.code === 'ERROR_CEREMONY_ABORTED'
}

/** Registers a passkey for the signed-in customer. Answers the new Me, or null when they cancelled. */
export async function registerPasskey(): Promise<Me | null> {
  const optionsJSON = await api<PublicKeyCredentialCreationOptionsJSON>('/api/passkeys/register/options', { method: 'POST', json: {} })
  let response
  try { response = await startRegistration({ optionsJSON }) } catch (error) { if (cancelled(error)) return null; throw error }
  return api<Me>('/api/passkeys/register/verify', { method: 'POST', json: { response } })
}

/** Signs in with a passkey. Answers Me, or null when they cancelled. */
export async function signInWithPasskey(): Promise<Me | null> {
  const optionsJSON = await api<PublicKeyCredentialRequestOptionsJSON>('/api/passkeys/login/options', { method: 'POST', json: {}, quiet401: true })
  let response
  try { response = await startAuthentication({ optionsJSON }) } catch (error) { if (cancelled(error)) return null; throw error }
  return (await api<{ me: Me }>('/api/passkeys/login/verify', { method: 'POST', json: { response }, quiet401: true })).me
}

const SOCIALS: readonly Social[] = platform === 'android' ? ['google', 'apple', 'facebook', 'microsoft'] : ['apple', 'google', 'facebook', 'microsoft']

function SocialButtons({ onCode, onPasskey }: { readonly onCode: () => void; readonly onPasskey: (() => void) | null }) {
  const app = useApp()
  const { t } = app
  const label = { apple: t.socApple, google: t.socGoogle, facebook: t.socFacebook, microsoft: t.socMicrosoft }
  return (
    <>
      {SOCIALS.map((k) => (
        <button key={k} class={`v-soc v-soc--${k}`} type="button" onClick={() => app.sheet(<SocialSheet k={k} onCode={onCode} onPasskey={onPasskey} />)}>
          <SocialIcon k={k} />
          <span>{label[k]}</span>
        </button>
      ))}
    </>
  )
}

const passkeyWord = <K extends 'pkTitle' | 'pkCta' | 'pkDone' | 'siPasskey'>(t: ReturnType<typeof useApp>['t'], key: K): string =>
  t[`${key}${platform === 'ios' ? 'Ios' : platform === 'android' ? 'And' : 'Key'}`]

// ───────────── welcome and sign-up ─────────────

export function Welcome() {
  const app = useApp()
  const { t, flow, me } = app
  const policyNumber = me?.policyNumber ?? flow?.policyNumber ?? null
  return (
    <>
      <Brand variant="lg" />
      {policyNumber && <span class="v-pill v-pill--ok" style={{ alignSelf: 'flex-start' }}><Icon n="check" s={16} /><span class="v-num">{fill(t.wPolicy, { n: policyNumber })}</span></span>}
      <svg class="v-hero-art" viewBox="0 0 360 168" aria-hidden="true">
        <rect x="0" y="0" width="360" height="168" rx="24" style={{ fill: 'var(--brand-soft)' }} />
        <rect x="30" y="98" width="20" height="42" rx="10" style={{ fill: 'var(--surface)' }} />
        <rect x="62" y="80" width="20" height="60" rx="10" style={{ fill: 'var(--q2)' }} />
        <rect x="94" y="88" width="20" height="52" rx="10" style={{ fill: 'var(--q2)' }} />
        <rect x="126" y="66" width="20" height="74" rx="10" style={{ fill: 'var(--prog)' }} />
        <rect x="158" y="72" width="20" height="68" rx="10" style={{ fill: 'var(--prog)' }} />
        <rect x="190" y="48" width="20" height="92" rx="10" style={{ fill: 'var(--prog)' }} />
        <circle cx="286" cy="84" r="40" style={{ fill: 'none', stroke: 'var(--surface)', strokeWidth: 17 }} />
        <path d="M286 44 A40 40 0 1 1 248.4 97.7" style={{ fill: 'none', stroke: 'var(--prog)', strokeWidth: 17, strokeLinecap: 'round' }} />
      </svg>
      <div class="v-stack-s">
        <p class="v-eyebrow">{t.wEyebrow}</p>
        <h1 class="v-h1">{t.wTitle}</h1>
        <p class="v-lead">{t.wBody}</p>
      </div>
      <ol class="v-stack-s">
        {t.wSteps.map((ws) => (
          <li key={ws.n} class="v-card v-card--tight" style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
            <span style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'var(--brand)', color: 'var(--brand-ink)', display: 'grid', placeItems: 'center', fontWeight: 600, fontSize: '14px', flex: 'none' }}>{ws.n}</span>
            <span class="v-stack-s" style={{ gap: '2px' }}><span class="v-h3">{ws.title}</span><span class="v-small">{ws.body}</span></span>
          </li>
        ))}
      </ol>
      <div class="v-msg v-msg--brand"><Icon n="eyeOff" /><span>{t.wPrivacy}</span></div>
      <div class="v-stack-s">
        <button class="v-btn v-btn--primary" type="button" onClick={() => (me ? void app.nextStep(me).then((p) => app.nav(p)) : app.nav('/signup'))}>{t.start}<Icon n="arrow" /></button>
        {!me && <button class="v-btn v-btn--quiet" type="button" onClick={() => app.nav('/signin')}>{t.wSignin}</button>}
      </div>
    </>
  )
}

/** Seconds left until an ISO time, ticking every second. */
function useSecondsLeft(iso: string | null): number {
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    if (!iso) return
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [iso])
  return iso ? Math.max(0, Math.ceil((Date.parse(iso) - now) / 1000)) : 0
}

export function Signup() {
  const app = useApp()
  const { t, flow } = app
  const left = useSecondsLeft(flow?.expiresAt ?? null)
  const toCode = () => app.nav('/verify')
  return (
    <>
      {flow?.policyNumber && (
        <div class="v-card v-card--flat v-stack-s" style={{ padding: '16px' }}>
          <div class="v-between"><span class="v-xs">{t.suActivating}</span><span class="v-pill" aria-label={fill(t.expiresIn, { t: mmss(left) })}><Icon n="clock" s={16} /><span class="v-num">{mmss(left)}</span></span></div>
          <div class="v-h2 v-num">{flow.policyNumber}</div>
          <div class="v-cluster v-small" style={{ gap: '16px' }}>
            {flow.maskedEmail && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><Icon n="mail" s={16} />{flow.maskedEmail}</span>}
            {flow.maskedPhone && <span class="v-num" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><Icon n="phone" s={16} />{flow.maskedPhone}</span>}
          </div>
          <p class="v-xs" style={{ display: 'flex', gap: '6px', alignItems: 'flex-start' }}><Icon n="lock" s={16} style={{ marginTop: '1px' }} /><span>{t.suLinkNote}</span></p>
        </div>
      )}
      <div class="v-stack-s">
        <h1 class="v-h1">{t.suTitle}</h1>
        <p class="v-lead">{t.suSub}</p>
      </div>
      <div class="v-stack-s" style={{ gap: '10px' }}>
        <SocialButtons onCode={toCode} onPasskey={passkeysWork() ? toCode : null} />
        <div class="v-or" style={{ margin: '6px 0' }}>{t.or}</div>
        <button class="v-btn v-btn--secondary" type="button" onClick={toCode}><Icon n="mail" />{t.suEmail}</button>
        {passkeysWork() && <button class="v-btn v-btn--ghost" type="button" onClick={toCode}><Icon n="passkey" />{t.suPasskey}</button>}
      </div>
      <p class="v-xs">{t.suLegal}</p>
    </>
  )
}

/** Choose where the policy-link code goes, from the masked contacts on the policy. */
export function LinkPick() {
  const app = useApp()
  const { t, flow } = app
  const options = [
    flow?.maskedEmail ? { channel: 'email' as const, label: t.lpEmail, sub: flow.maskedEmail, icon: 'mail' as const } : null,
    flow?.maskedPhone ? { channel: 'sms' as const, label: t.lpSms, sub: flow.maskedPhone, icon: 'phone' as const } : null,
  ].filter((o) => o !== null)
  const [channel, setChannel] = useState<'email' | 'sms'>(flow?.channel ?? options[0]?.channel ?? 'email')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const send = async () => {
    setBusy(true)
    setError('')
    try {
      app.setFlow(await api<FlowView>('/api/flow/code', { method: 'POST', json: { channel } }))
      app.nav('/code')
    } catch (e) {
      if (e instanceof ApiError && e.code === 'resendTooSoon') app.nav('/code')
      else if (e instanceof ApiError && e.code === 'noFlow') app.showProblem({ key: 'noFlow' })
      else setError(t.errGeneric)
    } finally { setBusy(false) }
  }
  return (
    <>
      <div class="v-stack-s">
        <h1 class="v-h1">{t.lpTitle}</h1>
        <p class="v-lead">{t.lpBody}</p>
      </div>
      <fieldset style={{ border: 0, padding: 0, margin: 0 }} class="v-stack-s">
        <legend class="v-label" style={{ marginBottom: '8px' }}>{t.lpLegend}</legend>
        <div class="v-choices">
          {options.map((o) => (
            <label key={o.channel} class="v-choice">
              <input type="radio" name="channel" checked={channel === o.channel} onChange={() => setChannel(o.channel)} />
              <span class="v-ico" style={{ width: '36px', height: '36px' }}><Icon n={o.icon} /></span>
              <span class="v-choice-main"><span>{o.label}</span><span class="v-choice-sub v-num">{o.sub}</span></span>
            </label>
          ))}
        </div>
      </fieldset>
      {error && <div class="v-msg v-msg--accent" role="alert"><Icon n="info" /><span>{error}</span></div>}
      <button class="v-btn v-btn--primary" type="button" onClick={() => void send()} disabled={busy || options.length === 0}>{busy ? <Spinner /> : null}{t.lpSend}</button>
      <p class="v-xs" style={{ display: 'flex', gap: '8px' }}><Icon n="lock" s={16} style={{ marginTop: '1px' }} /><span>{t.lpWhy}</span></p>
    </>
  )
}

// ───────────── the code ─────────────

export function Code() {
  const app = useApp()
  const { t, flow } = app
  const [code, setCode] = useState('')
  const [state, setState] = useState<OtpState>('idle')
  const [triesLeft, setTriesLeft] = useState(flow?.triesLeft ?? 5)
  const [resendAt, setResendAt] = useState(flow?.resendAt ?? null)
  const wait = useSecondsLeft(resendAt)
  const signin = flow?.purpose === 'signin'
  const to = flow?.channel === 'sms' ? flow.maskedPhone : flow?.channel === 'email' ? flow.maskedEmail : null
  const minutes = flow?.codeSentAt && flow.codeExpiresAt ? Math.round((Date.parse(flow.codeExpiresAt) - Date.parse(flow.codeSentAt)) / 60_000) : 10
  const other = flow?.channel === 'email' ? (flow.maskedPhone ? 'sms' : null) : flow?.channel === 'sms' ? (flow.maskedEmail ? 'email' : null) : null

  useEffect(() => { setResendAt(flow?.resendAt ?? null) }, [flow?.resendAt])

  const verify = async (value: string) => {
    setState('checking')
    try {
      const result = await api<{ next: 'passkey' | 'home'; me: Me }>('/api/flow/verify', { method: 'POST', json: { code: value }, quiet401: true })
      setState('ok')
      window.setTimeout(() => {
        app.setFlow(null)
        app.setMe(result.me)
        if (result.next === 'passkey' && passkeysWork()) app.nav('/passkey', { replace: true })
        else void app.nextStep(result.me).then((p) => app.nav(p, { replace: true }))
      }, 900)
    } catch (e) {
      if (e instanceof ApiError && e.code === 'wrongCode') {
        setTriesLeft(Number(e.body['triesLeft'] ?? triesLeft - 1))
        setState('wrong')
        window.setTimeout(() => setCode(''), 400)
      } else if (e instanceof ApiError && e.code === 'codeExpired') app.showProblem({ key: 'codeExpired' })
      else if (e instanceof ApiError && e.code === 'locked') app.showProblem({ key: 'locked', until: String(e.body['lockedUntil'] ?? '') })
      else if (e instanceof ApiError && (e.code === 'noFlow' || e.status === 401)) app.showProblem({ key: 'noFlow' })
      else { setState('idle'); app.toast(t.errGeneric) }
    }
  }
  const onChange = (v: string) => {
    setCode(v)
    if (state === 'wrong') setState('idle')
    if (v.length === 6) void verify(v)
  }
  const resend = async (channel: 'email' | 'sms') => {
    try {
      const next = await api<FlowView>('/api/flow/code', { method: 'POST', json: { channel } })
      app.setFlow(next)
      setCode('')
      setState('idle')
      app.toast(t.cdNew)
    } catch (e) {
      if (e instanceof ApiError && e.code === 'resendTooSoon') setResendAt(String(e.body['resendAt'] ?? ''))
      else if (e instanceof ApiError && e.code === 'locked') app.showProblem({ key: 'locked', until: String(e.body['lockedUntil'] ?? '') })
      else if (e instanceof ApiError && e.code === 'noFlow') app.showProblem({ key: 'noFlow' })
      else app.toast(t.errGeneric)
    }
  }
  return (
    <>
      <div class="v-stack-s">
        <h1 class="v-h1">{t.cdTitle}</h1>
        <p class="v-lead">{to ? fill(t.cdSent, { to, m: minutes }) : fill(t.cdSentAny, { m: minutes })}</p>
      </div>
      <Otp value={code} onChange={onChange} state={state} label={t.cdLabel} />
      <div aria-live="polite" class="v-stack-s">
        {state === 'checking' && <p class="v-small" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}><Spinner />{t.cdChecking}</p>}
        {state === 'ok' && <div class="v-msg v-msg--ok v-pop"><Icon n="check" /><span>{signin ? t.cdOkSi : t.cdOk}</span></div>}
        {state === 'wrong' && <div class="v-msg v-msg--accent"><Icon n="info" /><span>{triesLeft === 1 ? t.cdWrong1 : fill(t.cdWrong, { n: triesLeft })}</span></div>}
      </div>
      {flow?.channel && (
        <div class="v-between" style={{ flexWrap: 'wrap' }}>
          {wait > 0
            ? <span class="v-xs v-num" style={{ minHeight: '44px', display: 'inline-flex', alignItems: 'center' }}>{fill(t.cdResendIn, { t: mmss(wait) })}</span>
            : <button class="v-link" type="button" onClick={() => flow.channel && void resend(flow.channel)}>{t.cdResend}</button>}
          {other && <button class="v-link" type="button" disabled={wait > 0} onClick={() => void resend(other)}>{other === 'sms' ? t.cdToSms : t.cdToEmail}</button>}
        </div>
      )}
      <Inbox path="/api/flow/inbox" onUse={(c) => onChange(c)} />
    </>
  )
}

// ───────────── passkey offer, right after the first sign-in ─────────────

export function Passkey() {
  const app = useApp()
  const { t } = app
  const [state, setState] = useState<'off' | 'busy' | 'on'>('off')
  const [error, setError] = useState('')
  const next = () => app.me && void app.nextStep(app.me).then((p) => app.nav(p))
  const enable = async () => {
    setState('busy')
    setError('')
    try {
      const me = await registerPasskey()
      if (me) { app.setMe(me); setState('on') } else setState('off')
    } catch { setState('off'); setError(t.pkErr) }
  }
  if (state === 'on') {
    return (
      <div class="v-stack-l" style={{ alignItems: 'center', textAlign: 'center', paddingTop: '24px' }}>
        <div class="v-medal"><Icon n="check" style={{ width: '44px', height: '44px', color: 'var(--brand-text)', strokeWidth: 2.2 }} /></div>
        <div class="v-stack-s"><h1 class="v-h1">{passkeyWord(t, 'pkDone')}</h1><p class="v-lead">{t.pkDoneBody}</p></div>
        <button class="v-btn v-btn--primary" type="button" onClick={next}>{t.cont}</button>
      </div>
    )
  }
  return (
    <div class="v-stack-l" style={{ paddingTop: '16px' }}>
      <span class="v-ico v-ico--brand v-ico--lg" style={{ width: '72px', height: '72px', borderRadius: '22px' }}><Icon n="passkey" style={{ width: '34px', height: '34px' }} /></span>
      <div class="v-stack-s"><h1 class="v-h1">{passkeyWord(t, 'pkTitle')}</h1><p class="v-lead">{t.pkBody}</p></div>
      <ul class="v-card v-list">
        {t.pkBullets.map((b) => <li key={b} class="v-row"><span class="v-ico v-ico--ok" style={{ width: '32px', height: '32px', borderRadius: '10px' }}><Icon n="check" s={16} /></span><span class="v-small" style={{ color: 'var(--ink)' }}>{b}</span></li>)}
      </ul>
      {error && <div class="v-msg v-msg--accent" role="alert"><Icon n="info" /><span>{error}</span></div>}
      <div class="v-stack-s">
        <button class="v-btn v-btn--primary" type="button" onClick={() => void enable()} disabled={state === 'busy'}>{state === 'busy' ? t.pkBusy : passkeyWord(t, 'pkCta')}</button>
        <button class="v-btn v-btn--quiet" type="button" onClick={next}>{t.notNow}</button>
      </div>
    </div>
  )
}

// ───────────── sign in ─────────────

export function Signin() {
  const app = useApp()
  const { t } = app
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const passkey = async () => {
    setBusy(true)
    setError('')
    try {
      const me = await signInWithPasskey()
      if (me) { app.setMe(me); app.nav(await app.nextStep(me), { replace: true }) }
    } catch { setError(t.siPkErr) } finally { setBusy(false) }
  }
  const works = passkeysWork()
  return (
    <>
      <Brand variant="lg" />
      <div class="v-stack-s"><h1 class="v-h1">{t.siTitle}</h1><p class="v-lead">{t.siBody}</p></div>
      {works && <button class="v-btn v-btn--primary" type="button" onClick={() => void passkey()} disabled={busy} style={{ minHeight: '56px' }}>{busy ? <Spinner /> : <Icon n="passkey" />}{passkeyWord(t, 'siPasskey')}</button>}
      {error && <div class="v-msg v-msg--accent" role="alert"><Icon n="info" /><span>{error}</span></div>}
      {works && <div class="v-or">{t.or}</div>}
      <div class="v-stack-s" style={{ gap: '10px' }}>
        <SocialButtons onCode={() => app.nav('/signin/code')} onPasskey={works ? () => void passkey() : null} />
        <button class="v-btn v-btn--secondary" type="button" onClick={() => app.nav('/signin/code')}><Icon n="mail" />{t.siCode}</button>
      </div>
      <button class="v-btn v-btn--quiet" type="button" onClick={() => app.nav('/activate')}>{t.siActivate}</button>
    </>
  )
}

/** A flow started from a typed contact knows its channel: ask for the code at once (a refusal keeps the flow as it is). */
const sendIfNeeded = async (flow: FlowView): Promise<FlowView> =>
  flow.codeSentAt || !flow.channel ? flow : api<FlowView>('/api/flow/code', { method: 'POST', json: { channel: flow.channel }, quiet401: true }).catch(() => flow)

const looksLikeContact = (v: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || v.replace(/[\s().-]/g, '').replace(/^\+/, '').replace(/\D/g, '').length >= 9

/** Sign in with a code: the customer types the email or phone of their policy. */
export function SigninCode() {
  const app = useApp()
  const { t } = app
  const [contact, setContact] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const submit = async (e: Event) => {
    e.preventDefault()
    if (!looksLikeContact(contact.trim())) { setError(t.scInvalid); return }
    setBusy(true)
    try {
      app.setFlow(await sendIfNeeded(await api<FlowView>('/api/signin/start', { method: 'POST', json: { contact: contact.trim() }, quiet401: true })))
      app.nav('/code')
    } catch { setError(t.errGeneric) } finally { setBusy(false) }
  }
  return (
    <form class="v-stack-l" onSubmit={(e) => void submit(e)} noValidate>
      <span class="v-ico v-ico--brand v-ico--lg"><Icon n="mail" /></span>
      <div class="v-stack-s"><h1 class="v-h1">{t.scTitle}</h1><p class="v-lead">{t.scBody}</p></div>
      <label class="v-field">
        <span class="v-label">{t.acContact}</span>
        <input class="v-input" value={contact} onInput={(e) => { setContact(e.currentTarget.value); setError('') }} autocomplete="username" inputMode="email" aria-invalid={error ? 'true' : 'false'} aria-describedby={error ? 'sc-err' : undefined} />
        {error && <span id="sc-err" class="v-err">{error}</span>}
      </label>
      <button class="v-btn v-btn--primary" type="submit" disabled={busy}>{busy ? <Spinner /> : null}{t.lpSend}</button>
      <p class="v-xs" style={{ display: 'flex', gap: '8px' }}><Icon n="lock" s={16} style={{ marginTop: '1px' }} /><span>{t.acNote}</span></p>
    </form>
  )
}

/** Activate without the link: policy number and the email or phone on it. The answer is always the same. */
export function Activate() {
  const app = useApp()
  const { t } = app
  const [policyNumber, setPolicyNumber] = useState('')
  const [contact, setContact] = useState('')
  const [errors, setErrors] = useState<{ n?: string; c?: string }>({})
  const [busy, setBusy] = useState(false)
  const numberOk = policyNumber.trim().length >= 4
  const contactOk = looksLikeContact(contact.trim())
  const submit = async (e: Event) => {
    e.preventDefault()
    const found = { ...(numberOk ? {} : { n: t.acNumErr }), ...(contactOk ? {} : { c: t.scInvalid }) }
    setErrors(found)
    if (!numberOk || !contactOk) return
    setBusy(true)
    try {
      app.setFlow(await sendIfNeeded(await api<FlowView>('/api/activation/without-link', { method: 'POST', json: { policyNumber: policyNumber.trim().toUpperCase(), contact: contact.trim() }, quiet401: true })))
      app.nav('/activate/sent')
    } catch { app.toast(t.errGeneric) } finally { setBusy(false) }
  }
  return (
    <form class="v-stack-l" onSubmit={(e) => void submit(e)} noValidate>
      <div class="v-stack-s"><h1 class="v-h1">{t.acTitle}</h1><p class="v-lead">{t.acBody}</p></div>
      <label class="v-field">
        <span class="v-label">{t.acNum}</span>
        <span class="v-input-wrap"><input class="v-input v-num" value={policyNumber} onInput={(e) => setPolicyNumber(e.currentTarget.value)} autocomplete="off" autocapitalize="characters" aria-invalid={errors.n ? 'true' : 'false'} aria-describedby="ac-num-help" />{numberOk && <span class="v-input-ok"><Icon n="check" /></span>}</span>
        {errors.n ? <span class="v-err">{errors.n}</span> : <span id="ac-num-help" class="v-help">{t.acNumHelp}</span>}
      </label>
      <label class="v-field">
        <span class="v-label">{t.acContact}</span>
        <span class="v-input-wrap"><input class="v-input" value={contact} onInput={(e) => setContact(e.currentTarget.value)} autocomplete="email" inputMode="email" aria-invalid={errors.c ? 'true' : 'false'} />{contactOk && <span class="v-input-ok"><Icon n="check" /></span>}</span>
        {errors.c && <span class="v-err">{errors.c}</span>}
      </label>
      <button class="v-btn v-btn--primary" type="submit" disabled={busy}>{busy ? <Spinner /> : null}{t.lpSend}</button>
      <p class="v-xs" style={{ display: 'flex', gap: '8px' }}><Icon n="lock" s={16} style={{ marginTop: '1px' }} /><span>{t.acNote}</span></p>
    </form>
  )
}

export function ActivateSent() {
  const app = useApp()
  const { t } = app
  return (
    <div class="v-stack-l" style={{ paddingTop: '8px' }}>
      <span class="v-ico v-ico--brand v-ico--lg"><Icon n="mail" /></span>
      <div class="v-stack-s"><h1 class="v-h1">{t.asTitle}</h1><p class="v-lead">{t.asBody}</p></div>
      <button class="v-btn v-btn--primary" type="button" onClick={() => app.nav('/code')}>{t.asEnter}</button>
      <button class="v-btn v-btn--quiet" type="button" onClick={() => app.goBack('/activate')}>{t.back}</button>
    </div>
  )
}

// ───────────── error and status screens ─────────────

const ICONS = { clock: 'clock', lock: 'lock', link: 'link', info: 'info', user: 'user', cal: 'cal' } as const

export function Problem() {
  const app = useApp()
  const { t, problem, flow, lang } = app
  const key = problem?.key ?? 'noFlow'
  const e = t.errs[key]
  const contact = () => app.sheet(<ContactSheet />)
  const newCode = async () => {
    try {
      app.setFlow(await api<FlowView>('/api/flow/code', { method: 'POST', json: { channel: flow?.channel ?? 'email' } }))
      app.nav('/code', { replace: true })
    } catch { app.nav('/signin', { replace: true }) }
  }
  const run: Record<string, () => void> = {
    linkExpired: () => app.nav('/activate'), codeExpired: () => void newCode(), locked: () => app.nav('/signin', { replace: true }),
    alreadyActive: () => app.nav('/signin', { replace: true }), notActive: contact, cancelled: contact, noFlow: () => app.nav('/signin', { replace: true }),
    signedOut: () => app.nav('/signin', { replace: true }), signedOutAll: () => app.nav('/signin', { replace: true }),
  }
  const alt: Record<string, () => void> = {
    linkExpired: () => app.nav('/signin'), codeExpired: () => app.nav(flow?.purpose === 'signin' ? '/signin/code' : '/verify'), locked: contact,
    notActive: () => app.nav('/signin'), cancelled: () => app.nav('/signin'), noFlow: () => app.nav('/activate'),
  }
  const ico = e.i === 'lock' ? 'v-ico--brand' : e.i === 'clock' ? 'v-ico--accent' : ''
  const minutes = Math.round((app.me?.idleSeconds ?? 900) / 60)
  const note = e.note && problem?.until ? fill(e.note, { t: fmtTime(problem.until, lang) }) : ''
  return (
    <div class="v-stack-l" style={{ paddingTop: '16px' }}>
      <span class={`v-ico v-ico--lg ${ico}`}><Icon n={ICONS[e.i as keyof typeof ICONS] ?? 'info'} /></span>
      <div class="v-stack-s"><h1 class="v-h1">{e.title}</h1><p class="v-lead">{fill(e.body, { n: minutes })}</p></div>
      {note && <div class="v-msg"><Icon n="clock" /><span class="v-num">{note}</span></div>}
      <div class="v-stack-s">
        <button class="v-btn v-btn--primary" type="button" onClick={run[key]}>{e.cta}</button>
        {e.alt && alt[key] && <button class="v-btn v-btn--quiet" type="button" onClick={alt[key]}>{e.alt}</button>}
      </div>
    </div>
  )
}

export function Deleted() {
  const app = useApp()
  const { t } = app
  return (
    <div class="v-stack-l" style={{ paddingTop: '16px' }}>
      <span class="v-ico v-ico--ok v-ico--lg"><Icon n="check" /></span>
      <div class="v-stack-s"><h1 class="v-h1">{t.delTitle}</h1><p class="v-lead">{t.delBody}</p></div>
      <button class="v-btn v-btn--primary" type="button" onClick={() => app.nav('/signin', { replace: true })}>{t.delHome}</button>
    </div>
  )
}
